import { RawDraftContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import React, { useEffect, useState } from 'react';
import { isIOS } from 'react-device-detect';
import ReactPlayer from 'react-player';
import { useParams } from 'react-router-dom';
import { mixed, object, string } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { Campo, Editor, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import UnidadeDeAprendizagem from 'Interface/UnidadesDeAprendizagem';
import {
  useRecuperarCategoriasQuery,
} from 'Service';
import {
  useEditarUnidadeDeAprendizagemMutation,
  useRecuperarUnidadeDeAprendizagemQuery,
} from 'View/Service/Default/UnidadesDeAprendizagem';

import Bibliografias from './Bibliografias';
import DicasMais from './DicasMais';
import Legendas from './Legendas';
import Materiais from './Materiais';
import Quizzes from './Quizzes';

const regras = object({
  titulo: string().required('Obrigatório'),
  descricao: mixed().test('obrigatorio2', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
  competencias: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
  video: string().url('Não é uma URL válida').required('Obrigatório'),
  video_apple: string().url('Não é uma URL válida').required('Obrigatório'),
});

const Editar = (): React.ReactElement => {
  const { id = 0 } = useParams<'id'>();
  const toast = useToast();

  const [aba, setAba] = useState<number>(0);

  const [editarUnidade] = useEditarUnidadeDeAprendizagemMutation();

  const { data } = useRecuperarCategoriasQuery({
    finder: 'listaJson',
    limit: 100,
    sort: 'nome',
  });

  const {
    data: { unidade_de_aprendizagem } = {},
    isFetching,
    refetch,
  } = useRecuperarUnidadeDeAprendizagemQuery({ id });

  const enviar = async (dados: UnidadeDeAprendizagem): Promise<void> => {
    try {
      await editarUnidade(dados).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refetch, []);

  return (
    <div className="animate__animated animate__fadeIn">
      <Cabecalho
        titulo="Editando unidade de aprendizagem"
        descricao="Altere o formulário e clique em enviar para editar a unidade de aprendizagem"
      />
      <div className="row">
        <div className="col-xl-12 col-xxl-1 vstack flex-row flex-xxl-column gap-2 mb-3">
          <Botao
            icone="house"
            tema={aba == 0 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(0)}
          />
          <Botao
            icone="journal-bookmark-fill"
            tema={aba == 1 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(1)}
          />
          <Botao
            icone="pencil"
            tema={aba == 2 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(2)}
          />
          <Botao
            icone="plus-lg"
            tema={aba == 3 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(3)}
          />
          <Botao
            icone="book-half"
            tema={aba == 4 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(4)}
          />
          <Botao
            icone="badge-cc-fill"
            tema={aba == 5 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(5)}
          />
        </div>
        <div className="col-xl-12 col-xxl-11">
          {aba == 0 && (
            <Form
              onSubmit={enviar}
              regras={regras}
              inicial={unidade_de_aprendizagem}
              desabilitado={isFetching}
            >
              <div className="row">
                <div className="col-xl-6 col-xxl-7">
                  <div className="bg-white shadow-sm rounded p-4">
                    <div className="row">
                      <div className="col-md-12">
                        <Campo name="titulo" rotulo="Título" />
                      </div>
                      <div className="col-md-12">
                          <Selecao
                            name="categoria_id"
                            rotulo="Categoria"
                            opcoes={data?.categorias}
                            prefixar={<Icone nome="view-list" />}
                          />
                        </div>
                      <div className="col-md-12">
                        <Campo
                          name="video"
                          rotulo="URL do vídeo (Android e Windows)"
                          ajuda="Deve estar em um formato compatível com DASH"
                          prefixar={<Icone nome="link" />}
                        />
                      </div>
                      <div className="col-md-12">
                        <Campo
                          name="video_apple"
                          rotulo="URL do vídeo (Apple)"
                          ajuda="Deve estar em um formato compatível com HLS para dispositivos Apple"
                          prefixar={<Icone nome="link" />}
                        />
                      </div>
                      <div className="col-md-12">
                        <Editor name="descricao" rotulo="Descrição" />
                      </div>
                      <div className="col-md-12">
                        <Editor name="competencias" rotulo="Competências" />
                      </div>
                    </div>
                    <Botao type="submit">Enviar</Botao>{' '}
                    <BotaoLink
                      tema="light"
                      to="/admin/unidades-de-aprendizagem"
                    >
                      Cancelar
                    </BotaoLink>
                  </div>
                </div>
                <div className="col-xl-6 col-xxl-5">
                  <div className="bg-dark rounded">
                    <ReactPlayer
                      url={unidade_de_aprendizagem?.video_apple}
                      config={{
                        file: {
                          forceHLS: !isIOS ? true : false,
                          hlsOptions: {
                            xhrSetup: (xhr: XMLHttpRequest) => {
                              xhr.withCredentials = true;
                            },
                          },
                          attributes: { crossOrigin: 'use-credentials' },
                        },
                      }}
                      width="100%"
                      playsinline
                      controls
                      light
                    />
                  </div>
                  <p className="fs-2 fw-bold mt-3 mb-2">
                    {unidade_de_aprendizagem?.titulo}
                  </p>
                  {unidade_de_aprendizagem && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: draftToHtml(
                          unidade_de_aprendizagem.descricao as unknown as RawDraftContentState
                        ),
                      }}
                    />
                  )}
                </div>
              </div>
            </Form>
          )}
          {aba == 1 && <Materiais />}
          {aba == 2 && <Quizzes />}
          {aba == 3 && <DicasMais />}
          {aba == 4 && <Bibliografias />}
          {aba == 5 && <Legendas />}
        </div>
      </div>
    </div>
  );
};

export default Editar;
